import React, {
  useState, useEffect, useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import StatusBar from '../../../components/status-bar/status-bar.component';
import Loader from '../../../components/loader/loader.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import VerifyPhoneModal from '../../../components/verify-phone-modal/verify-phone-modal.component';
import LimitedPageContainer from '../../../components/limited-page-block/limited-page-container.component';
import Form from '../../../components/form/form.component';
import FormContainer from '../../../components/form-container/form-container.component';
import FormError from '../../../components/form-error/form-error.component';
import ControlButtonBlock from '../../app/common/control-button-block/control-button-block.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsUserSecurity
  from '../settings-user/settings-user-internal/settings-user-security/settings-user-security.component';
import SettingsSecurityValidator from './settings-security.validator';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import useEditModeQuery from '../../../hooks/use-edit-mode-query';
import useScreenMobile from '../../../hooks/use-screen-mobile';
import useFormBlock from '../../../hooks/use-form-block';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import { closeUserSessions } from '../../app/content/logout/logout.action';
import {
  getSettingsData, getUserApiKey, resetUserApiKey, resetUserSettings,
} from '../settings.action';
import { toggleEditMode } from '../../app/controls/controls.action';
import { getListDealData } from '../../app/search-list/search-list.action';
import { resetFormErrors } from '../../app/app.action';
import {
  deleteOtpSecretData, generateOtpSecretData, getOtpSecretData, sendPhoneVerificationCode, verifyUserOtp,
  verifyUserPhone,
} from '../settings-user/settings-user.action';
import saveSecuritySettings from './settings-security.action';
import { FORM_ID_SETTINGS_SECURITY } from '../../../constants/system/form-ids';
import { SETTINGS_PROFILE_EDIT } from '../../../constants/entity/permissions';
import { TAB_NAME_ACCOUNT_ACTIVITY, TAB_NAME_SECURITY } from '../../../constants/system/tab-names';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP } from '../../../constants/entity/verification-types';

const SettingsSecurity: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    userApiKey,
    userData,
    otpSecretHash,
    otpSecretQrCodeLink,
    actionHash,
    verificationTypeList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const dispatch: ReduxDispatch = useDispatch();
  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();
  const isMobileScreen: boolean = useScreenMobile();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isCloseUserSessionsConfirmModalOpen, setIsCloseUserSessionsConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isResetSettingsConfirmModalOpen, setIsResetSettingsConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isVerifyPhoneModalOpen, setIsVerifyPhoneModalOpen]: [boolean, Function] = useState(false);
  const [verifyPhoneModalErrorMessage, setVerifyPhoneModalErrorMessage]: [string | null, Function] = useState(null);
  const [userPhone, setUserPhone]: [string | null, Function] = useState(null);
  const [currentUserApiKey, setCurrentUserApiKey]: [string | null, Function] = useState<StateValueDefault>(null);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [connectOtpModalErrorMessage, setConnectOtpModalErrorMessage]: [string | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);

  const pageTitle: string = 'Settings: Security';
  const closeUserSessionsConfirmModalTitle: string = 'Terminate All Other Sessions';
  const closeUserSessionsConfirmModalText: string = 'Are you sure you want to terminate sessions on all other devices?';
  const resetSettingsConfirmModalTitle: string = 'Reset Settings';
  const resetSettingsConfirmModalText: string = 'Are you sure you want to restore settings to defaults?';
  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const isUserApiKeyChanged: boolean = !!userApiKey && (!userData || (!!userData && userData.apiKey !== userApiKey));
  const isFormDataPristine: boolean = isFormPristine && !isUserApiKeyChanged;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isUserApiKeyChanged;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && !!lastLocation;
  const userId: number = userData && userData.id;
  const userApiKeyValue: string | null = userApiKey || (userData && userData.apiKey) || null;
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const settingsProfileEditPermissions: string[] = useMemo((): string[] => [SETTINGS_PROFILE_EDIT], []);

  const onDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealData({
      query,
    }));
  }, []);

  const onDealSearchClose: Function = useCallback((): void => {
    dispatch(getListDealData({
      query: null,
    }));
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    dispatch(toggleEditMode(false));

    if (isUserApiKeyChanged) {
      dispatch(resetUserApiKey(false));
    }
  }, [isUserApiKeyChanged]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, [isUserApiKeyChanged]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    if (isUserApiKeyChanged) {
      dispatch(resetUserApiKey(false));
    }

    return true;
  }, [isFormChanged, isUserApiKeyChanged, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const userFormItem: FormValueItem = {
      ...values,
    };

    delete userFormItem.passwordConfirm;

    if (!userFormItem.password) {
      delete userFormItem.password;
      delete userFormItem.oldPassword;
    }

    if (!userFormItem.verificationTypeDefault) {
      userFormItem.verificationTypeDefault = null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveSecuritySettings({
        userForm: {
          user: userFormItem,
        },
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, isUserApiKeyChanged]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, isUserApiKeyChanged, formValues, withFormErrors]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const closeUserSessionsConfirmModal: Function = useCallback((): void => {
    setIsCloseUserSessionsConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const closeResetSettingsConfirmModal: Function = useCallback((): void => {
    setIsResetSettingsConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onCloseUserSessions: Function = useCallback((): void => {
    setIsCloseUserSessionsConfirmModalOpen(true);
  }, []);

  const onResetSettings: Function = useCallback((): void => {
    setIsResetSettingsConfirmModalOpen(true);
  }, []);

  const onGetUserApiKey: Function = useCallback((): void => {
    dispatch(getUserApiKey());
  }, []);

  const onCopyUserApiKey: Function = useCallback((): void => {
    setStatusMessage('Copied to clipboard');
    setIsOpenMessage(true);
  }, []);

  const onConnectOtpData: Function = useCallback((): void => {
    dispatch(generateOtpSecretData({}, (): void => {
      setConnectOtpModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onVerifyOtpSecretData: Function = useCallback((verificationCode: string, onSuccess: Function): void => {
    if (!verificationCode) {
      return;
    }

    dispatch(verifyUserOtp({
      verificationForm: {
        code: verificationCode,
      },
    }, (): void => {
      setStatusMessage('Verified successfully');
      setIsOpenMessage(true);
      setConnectOtpModalErrorMessage(null);

      if (onSuccess) {
        onSuccess();
      }
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onDeleteOtpSecretData: Function = useCallback((onSuccess?: Function): void => {
    dispatch(deleteOtpSecretData({}, (): void => {
      setStatusMessage('Authenticator data deleted successfully');
      setIsOpenMessage(true);

      if (userData.verificationTypeDefault === VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP) {
        changeForm('verificationTypeDefault', '');
      }

      if (onSuccess) {
        onSuccess();
      }
    }));
  }, [userData]);

  const onGetOtpSecretData: Function = useCallback((): void => {
    dispatch(getOtpSecretData({}, (): void => {
      setConnectOtpModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setConnectOtpModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onVerifyPhone: Function = useCallback((phone: string): void => {
    if (!phone || !FormatService.isValidPhoneNumber(phone)) {
      return;
    }

    setIsVerifyPhoneModalOpen(true);
    setUserPhone(phone);

    dispatch(sendPhoneVerificationCode({
      phone,
    }, undefined, (exception: ServerError): void => {
      if (exception && exception.message) {
        setVerifyPhoneModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onVerifyPhoneConfirm: Function = useCallback((
    verificationCode: string, phone: string, onSuccess?: Function,
  ): void => {
    if (!verificationCode || !phone || !actionHash) {
      return;
    }

    dispatch(verifyUserPhone({
      verificationForm: {
        code: verificationCode,
        phone,
        actionHash,
      },
    }, (): void => {
      closeVerifyPhoneModal();

      setStatusMessage('Verified successfully');
      setIsOpenMessage(true);

      if (onSuccess) {
        onSuccess();
      }
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setVerifyPhoneModalErrorMessage(exception.message);
      }
    }));
  }, [actionHash]);

  const closeVerifyPhoneModal: Function = useCallback((): void => {
    setIsVerifyPhoneModalOpen(false);
    setVerifyPhoneModalErrorMessage(null);
    setUserPhone(null);
  }, []);

  const onCloseUserSessionsConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(closeUserSessions({
        userId,
      }, (response: ActionResponse): void => {
        setIsCloseUserSessionsConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setStatusMessage('All other sessions were terminated successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const onResetSettingsConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!userId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(resetUserSettings({
        userId,
      }, (response: ActionResponse): void => {
        setStatusMessage('Settings were reset to defaults successfully');
        setIsOpenMessage(true);

        setIsResetSettingsConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [userId]);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    dispatch(getSettingsData());
  }, []);

  useEffect((): void => {
    if (currentUserApiKey !== userApiKeyValue) {
      changeForm('apiKey', userApiKeyValue);
      setCurrentUserApiKey(userApiKeyValue);
    }
  }, [userApiKeyValue]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  return (
    <Form id={FORM_ID_SETTINGS_SECURITY} onChange={onFormChange}>
      <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
        <ContentHeader title={pageTitle} />
        <SettingsPageTabs
          isDisabled={isEditMode}
          isCompressed={isRightDrawerOpen}
          activeTab={TAB_NAME_SECURITY}
          currentUser={currentUser}
          onBeforeLocationChange={onBeforeLocationChange}
        />
        {
          !isDataLoaded && (
            <Loader isPageLoader withTopOffset />
          )
        }
        {
          isDataLoaded && (
            <Fragment>
              <Controls
                isSaveDisabled={isSaveDisabled}
                withNoEditMode={queryTab === TAB_NAME_ACCOUNT_ACTIVITY}
                editPermissions={settingsProfileEditPermissions}
                onCancelForm={onCancelForm}
                onSubmitForm={onSubmitForm}
              />
              <LimitedPageContainer isActive>
                <SettingsUserSecurity
                  isCurrentUser
                  isSaving={isFormSaving}
                  isSaveDisabled={isSaveDisabled}
                  isEditMode={isEditMode}
                  isRightDrawerOpen={isRightDrawerOpen}
                  isFormChanged={isFormChanged}
                  isUserApiKeyChanged={isUserApiKeyChanged}
                  userData={userData}
                  otpSecretHash={otpSecretHash}
                  otpSecretQrCodeLink={otpSecretQrCodeLink}
                  connectOtpModalErrorMessage={connectOtpModalErrorMessage}
                  verificationTypeList={verificationTypeList}
                  formError={formError}
                  currentUser={currentUser}
                  onCancelForm={onCancelForm}
                  onSubmitForm={onSubmitForm}
                  onCloseUserSessions={onCloseUserSessions}
                  onResetSettings={onResetSettings}
                  onGetUserApiKey={onGetUserApiKey}
                  onCopyUserApiKey={onCopyUserApiKey}
                  onConnectOtpData={onConnectOtpData}
                  onVerifyOtpSecretData={onVerifyOtpSecretData}
                  onDeleteOtpSecretData={onDeleteOtpSecretData}
                  onGetOtpSecretData={onGetOtpSecretData}
                  onVerifyPhone={onVerifyPhone}
                  onDealSearch={onDealSearch}
                  onDealSearchClose={onDealSearchClose}
                  onBeforeLocationChange={onBeforeLocationChange}
                />
              </LimitedPageContainer>
              {
                isEditMode && (
                  <div>
                    <ControlButtonBlock
                      isEditMode={isEditMode}
                      isSaveDisabled={isSaveDisabled}
                      withOffset={isRightDrawerOpen}
                      onCancelForm={onCancelForm}
                      onSubmitForm={onSubmitForm}
                    />
                    {
                      isFormSaving && isEditMode && (
                        <Loader isInline isSubmit isFloating isHidden={isMobileScreen} />
                      )
                    }
                    {
                      (!!formError || !!mainServerFormError) && !isFormSaving && (
                        <FormError
                          withShowError={isEditMode}
                          withLeftOffset={isRightDrawerOpen}
                          error={formError || mainServerFormError}
                        />
                      )
                    }
                  </div>
                )
              }
            </Fragment>
          )
        }
        <VerifyPhoneModal
          isOpen={isVerifyPhoneModalOpen}
          phone={userPhone}
          errorMessage={verifyPhoneModalErrorMessage}
          onVerifyPhoneConfirm={onVerifyPhoneConfirm}
          onCloseModal={closeVerifyPhoneModal}
        />
        <ConfirmModal
          isOpen={isCloseUserSessionsConfirmModalOpen}
          title={closeUserSessionsConfirmModalTitle}
          text={closeUserSessionsConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onCloseUserSessionsConfirm}
          onCloseModal={closeUserSessionsConfirmModal}
        />
        <ConfirmModal
          isOpen={isResetSettingsConfirmModalOpen}
          title={resetSettingsConfirmModalTitle}
          text={resetSettingsConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onResetSettingsConfirm}
          onCloseModal={closeResetSettingsConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onConfirm={onLeaveConfirm}
          onCloseModal={closeLeaveConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </PullToRefresh>
    </Form>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    settings,
    controls,
    rightDrawer,
  } = reducers;

  const userData: User = settings.settingsData && settings.settingsData.profileSettingsData;

  if (userData) {
    initialValues.oldPassword = '';
    initialValues.password = '';
    initialValues.passwordConfirm = '';
    initialValues.apiKey = userData.apiKey;
    initialValues.isVerificationRequired = userData.isVerificationRequired;
    initialValues.verificationTypeDefault = userData.verificationTypeDefault;
  }

  return {
    isDataLoaded: !!settings.settingsData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    userApiKey: settings.userApiKey,
    otpSecretQrCodeLink: settings.otpSecretQrCodeLink,
    otpSecretHash: settings.otpSecretHash,
    actionHash: settings.actionHash,
    verificationTypeList: settings.settingsData && settings.settingsData.verificationTypeList,
    userData,
  };
};

const SettingsSecurityForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_SECURITY}
      component={SettingsSecurity}
      validator={SettingsSecurityValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsSecurityForm);
